import _ from 'lodash';
import Quill from 'quill/core';
import { useEffect, useRef, useState } from 'react';
import getRelativeBoundingRect from './controls/toolbar/getRelativeBoundingRect';
import type { Blot, Boundary } from './types';
import listenBody from './utils/listenBody';
import getReferenceNodeForBounds from './utils/getReferenceNodeForBounds';

const Module = Quill.import('core/module');
export const ignoreHoveredBlotLeaveClassName = 'ql-ignore-hovered-blot-leave';

const shouldIgnoreNode = (node: Node) => {
  if (!(node instanceof Element)) return true;
  return !!node.closest(`.${ignoreHoveredBlotLeaveClassName}`);
};

const EVENTS = {
  HOVERED: 'hovered-blot-listener-hovered',
  PERSIST: 'hovered-blot-listener-persist',
};

type State = {
  id: string;
  hoveredBlot: Blot;
};

export type HoveredBlotListenerSetting<
  Context extends Record<string, unknown>,
> = {
  getHoveredBlot(
    hoveredBlot: Blot,
    args: { node: Node; quill: Quill },
  ): Blot | null;
  getContext(blot: Blot): Context;
};

const stateSame = (state1: State | null, state2: State | null) => {
  if (!state1 && !state2) return true;
  if (state1 && state2)
    return state1.id === state2.id && state1.hoveredBlot === state2.hoveredBlot;
  return false;
};

class HoveredBlotListener extends Module {
  private settings: Record<
    string,
    HoveredBlotListenerSetting<Record<string, unknown>>
  >;
  private persist: boolean;
  private state: State | null;

  constructor(quill: Quill, options: Record<string, unknown>) {
    super(quill, options);

    this.settings = {};

    // Keeps the current hovered blot unchanged during mouse movement.
    // This is useful when the user is interacting with an associated view node.
    // ex dropdown menu is open.
    this.persist = false;
    this.state = null;

    this.setupListener();
  }

  addSetting(
    id: string,
    setting: HoveredBlotListenerSetting<Record<string, unknown>>,
  ) {
    this.settings[id] = setting;
  }

  removeSetting(id: string) {
    delete this.settings[id];
  }

  emitState() {
    const { state, quill, settings } = this;

    if (state) {
      const { id, hoveredBlot } = state;
      const referenceNode = getReferenceNodeForBounds(hoveredBlot);

      quill.emitter.emit(EVENTS.HOVERED, {
        id,
        hoveredBlot,
        bounds: getRelativeBoundingRect(referenceNode, quill.container),
        context: settings[id]?.getContext(hoveredBlot),
      });
    } else {
      quill.emitter.emit(EVENTS.HOVERED, {});
    }
  }

  setState(state: State | null) {
    if (stateSame(this.state, state)) return;

    this.persist = false;
    this.state = state;

    this.emitState();
  }

  getMatchedState(blot: Blot | null, node: Node): State | null {
    if (!blot) return null;

    const ids = Object.keys(this.settings);
    for (let i = 0; i < ids.length; i += 1) {
      const id = ids[i];
      if (id) {
        const setting = this.settings[id];
        const hoveredBlot = setting?.getHoveredBlot(blot, {
          node,
          quill: this.quill,
        });

        if (hoveredBlot) {
          return { id, hoveredBlot };
        }
      }
    }
    return null;
  }

  setupListener() {
    const { quill } = this;

    quill.scroll.domNode.addEventListener('mouseover', ({ target }) => {
      if ((this.persist && this.state) || !(target instanceof Node)) return;

      const blot = quill.scroll.find(target, true);
      this.setState(this.getMatchedState(blot, target));
    });

    listenBody(
      'mouseout',
      ({ relatedTarget }) => {
        if (this.persist || !this.state || !(relatedTarget instanceof Node))
          return;

        if (
          !quill.root.contains(relatedTarget) &&
          !shouldIgnoreNode(relatedTarget)
        ) {
          this.setState(null);
        }
      },
      quill,
    );

    quill.on(Quill.events.TEXT_CHANGE, () => {
      if (!this.state) return;

      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const existing = this.state.hoveredBlot.offset(quill.scroll) >= 0;
      if (existing) {
        this.emitState();
      } else {
        this.setState(null);
      }
    });

    quill.on(EVENTS.PERSIST, (persist: boolean) => {
      this.persist = persist;
    });
  }
}

type HoveredBlotListenerState<Context> = {
  bounds?: Boundary;
  hoveredBlot?: Blot;
  context?: Context;
};

export const useHoveredBlotListener = <Context extends Record<string, unknown>>(
  quill: Quill | null,
  setting: HoveredBlotListenerSetting<Context>,
) => {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = _.uniqueId('hoveredBlotListener');
  }

  const [state, setState] = useState<HoveredBlotListenerState<Context>>({});

  useEffect(() => {
    const hoveredBlotListenerModule = quill?.getModule(
      'hoveredBlotListener',
    ) as HoveredBlotListener | undefined;
    if (!hoveredBlotListenerModule) return undefined;

    const id = idRef.current as string;
    hoveredBlotListenerModule.addSetting(id, setting);
    return () => hoveredBlotListenerModule.removeSetting(id);
  }, [quill, setting]);

  useEffect(() => {
    if (!quill) return undefined;
    const listener = ({ id, ...args }: { id: string }) => {
      setState(id === idRef.current ? args : {});
    };

    quill.on(EVENTS.HOVERED, listener);
    return () => {
      quill.off(EVENTS.HOVERED, listener);
    };
  }, [quill]);

  const setPersist = (persist: boolean) =>
    quill?.emitter.emit(EVENTS.PERSIST, persist);

  return { ...state, setPersist };
};

export default HoveredBlotListener;
